import React, { useEffect, useState } from 'react';
import { TCompanyInfo, TSubscription } from '@/features/subscription/apiSlice';
import { BillingCoupon } from '@/features/subscription/components/subcomponents/BillingCoupon';
import { StripePlan } from '@/features/subscription/components/subcomponents/StripePlan';
import { Invoices } from '@/features/subscription/components/subcomponents/Invoices';
import { CompanyAddress } from '@/features/subscription/components/subcomponents/CompanyAddress';
import { CardDetails } from '@/features/subscription/components/subcomponents/CardDetails';
import { SendReceiptsTo } from '@/features/subscription/components/subcomponents/SendReceiptsTo';
import { ActivateButton } from '@/features/subscription/components/subcomponents/ActivateButton';
import { InvoiceHistory } from '@/features/subscription/components/subcomponents/InvoiceHistory';
import { PlanSummary } from '@/features/subscription/components/subcomponents/PlanSummary';
import { useGetNearestPriceTiersQuery } from '@/features/credits/apiSlice';
import { useDatasourceId } from '@/hooks';
import { equals } from 'ramda';

const EMPTY_INFO: TCompanyInfo = {
  company: '',
  street: '',
  city: '',
  postcode: '',
  country: '',
  taxNo: '',
};

export const ManageSubscription: React.FC<{
  subscription: TSubscription;
  onActivation: F0;
}> = ({ subscription, onActivation }) => {
  const [selectedPlanId, setSelectedPlan] = useState('');
  const datasourceId = useDatasourceId();
  const { isLoading } = useGetNearestPriceTiersQuery(datasourceId);

  useEffect(() => {
    if (subscription?.selectedPlan) {
      setSelectedPlan(subscription.selectedPlan);
    } else if (!selectedPlanId && subscription) {
      setSelectedPlan(subscription.stripePlans[0].id);
    }
  }, [subscription]);

  const [companyInfo, setCompanyInfo] = useState<TCompanyInfo>(EMPTY_INFO);

  useEffect(() => {
    if (subscription?.address) {
      const address = subscription.address;
      const copyCompanyInfo = Object.keys(address).reduce(
        (info, k) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          info[k] = address[k] || '';
          return info;
        },
        { ...EMPTY_INFO } as TCompanyInfo,
      );
      setCompanyInfo(copyCompanyInfo);
    }
  }, [subscription]);

  const [cardShortName, setCardShortName] = useState<string>('');

  useEffect(() => {
    if (subscription?.cardShortName) {
      setCardShortName(subscription.cardShortName);
    }
  }, [subscription?.cardShortName]);

  const selectedPlan = subscription.stripePlans.find(
    (p) => p.id === selectedPlanId,
  );

  if (!selectedPlan) return null;

  const showCompanyAddressWarning = Boolean(
    !isLoading && cardShortName && equals(companyInfo, EMPTY_INFO),
  );
  return (
    <>
      <BillingCoupon subscription={subscription} />
      <Invoices subscription={subscription} />
      <StripePlan
        subscription={subscription}
        selectedPlanId={selectedPlanId}
        onPlanChange={setSelectedPlan}
        selectedPlan={selectedPlan}
      />
      <CompanyAddress
        showWarning={showCompanyAddressWarning}
        companyInfo={companyInfo}
        updateCompanyInfo={setCompanyInfo}
      />
      <CardDetails
        cardShortName={cardShortName}
        stripeKey={subscription.stripeKey}
        onCardShortNameChange={setCardShortName}
      />
      <SendReceiptsTo
        subscription={subscription}
        cardShortName={cardShortName}
        hasAddress={!!companyInfo.company}
      />
      <ActivateButton
        selectedPlan={selectedPlan}
        disabled={!companyInfo.company || !cardShortName}
        companyInfo={companyInfo}
        coupon={subscription.coupon?.name}
        subscription={subscription}
        onActivation={onActivation}
      />
      {subscription.subscriptionStatus && <InvoiceHistory />}
      {!isGenericPlan(selectedPlan.id) && (
        <PlanSummary subscription={subscription} />
      )}
    </>
  );
};

const isGenericPlan = (planId: string): boolean => {
  return ['trevor_credit_pro_fixed', 'trevor_credit_enterprise_fixed'].includes(
    planId,
  );
};
